"use client"

import styled from "@emotion/styled"
import {
  Box,
  Checkbox,
  ClickAwayListener,
  FormControlLabel,
  IconButton,
  Popper,
  Typography,
} from "@mui/material"
import { useTranslations } from "next-intl"
import { useCallback, useRef, useState } from "react"
import { Control, Controller, FieldValues, Path } from "react-hook-form"
import ActiveCheckbox from "./icons/ActiveCheckbox"
import CheckboxIcon from "./icons/CheckboxIcon"
import CloseIcon from "./icons/CloseIcon"

const StyledContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 8px;
  width: 100%;
  padding-bottom: 24px;
`

const StyledLabel = styled(Typography)`
  color: #051711;
  font-size: 16px !important;
  font-weight: 500;
  line-height: 1.5 !important;
`

const StyledLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #008761;
  text-decoration: underline;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  font-family: inherit;

  &:hover {
    text-decoration: none;
  }

  &:focus {
    outline: 2px solid #008761;
    outline-offset: 2px;
  }
`

const StyledTooltip = styled(Box)`
  background-color: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.12);
  max-width: 320px;
  z-index: 1500;
`

const StyledTooltipHeader = styled(Box)`
  display: flex;
  margin-bottom: 8px;
`

const StyledTooltipContent = styled(Typography)`
  color: #051711;
  font-size: 16px !important;
  line-height: 1.5 !important;
  white-space: pre-line;
`

const StyledCloseButton = styled(IconButton)`
  padding: 4px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`

interface MarketingConsentProps<T extends FieldValues> {
  name: Path<T>
  control: Control<T>
  tooltipText: string
}

export const MarketingConsent = <T extends FieldValues>({
  name,
  control,
  tooltipText,
}: MarketingConsentProps<T>) => {
  const t = useTranslations("Forms")
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const linkRef = useRef<HTMLButtonElement>(null)
  const isTouchDevice =
    typeof window !== "undefined" && "ontouchstart" in window

  const handleLinkClick = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsTooltipOpen((prev) => !prev)
    },
    [],
  )

  const handleMouseEnter = useCallback(() => {
    if (!isTouchDevice) {
      setIsTooltipOpen(true)
    }
  }, [isTouchDevice])

  const handleMouseLeave = useCallback(() => {
    if (!isTouchDevice) {
      setIsTooltipOpen(false)
    }
  }, [isTouchDevice])

  const handleClose = useCallback(() => {
    setIsTooltipOpen(false)
  }, [])

  const handleClickAway = useCallback(() => {
    if (isTouchDevice) {
      setIsTooltipOpen(false)
    }
  }, [isTouchDevice])

  const checkboxId = `marketing-consent-${name}`

  // Split the consent text to insert the link
  const consentTextPart1 = t("marketingConsentPart1")
  const consentTextLink = t("marketingConsentLink")
  const consentTextPart2 = t("marketingConsentPart2")

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <ClickAwayListener onClickAway={handleClickAway}>
          <StyledContainer>
            <FormControlLabel
              htmlFor={checkboxId}
              control={
                <Checkbox
                  {...field}
                  id={checkboxId}
                  icon={<CheckboxIcon />}
                  checkedIcon={<ActiveCheckbox />}
                  checked={!!field.value}
                  sx={{
                    marginRight: "-8px",
                    paddingLeft: 0,
                    ":hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                  onChange={(e) => {
                    field.onChange(e.target.checked)
                  }}
                  inputProps={{
                    "aria-label": `${consentTextPart1} ${consentTextLink} ${consentTextPart2}`,
                  }}
                />
              }
              label={
                <StyledLabel component="span">
                  {consentTextPart1}
                  <StyledLink
                    ref={linkRef}
                    type="button"
                    onClick={handleLinkClick}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleLinkClick(e)
                      }
                    }}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    aria-describedby={
                      isTooltipOpen ? "marketing-tooltip" : undefined
                    }
                    aria-expanded={isTooltipOpen}
                    aria-haspopup="true"
                  >
                    {consentTextLink}
                  </StyledLink>
                  {consentTextPart2}
                </StyledLabel>
              }
              sx={{
                gap: "8px",
                marginLeft: 0,
                alignItems: "flex-start",
              }}
            />
            <Popper
              id="marketing-tooltip"
              open={isTooltipOpen}
              anchorEl={linkRef.current}
              placement="top"
              sx={{ zIndex: 1500 }}
              modifiers={[
                {
                  name: "offset",
                  options: {
                    offset: [0, 8],
                  },
                },
                {
                  name: "preventOverflow",
                  options: {
                    boundary: "viewport",
                    padding: 16,
                  },
                },
              ]}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <StyledTooltip role="tooltip">
                <StyledTooltipHeader>
                  <StyledCloseButton
                    onClick={handleClose}
                    aria-label={t("closeTooltip")}
                    size="small"
                  >
                    <CloseIcon />
                  </StyledCloseButton>
                </StyledTooltipHeader>
                <StyledTooltipContent>{tooltipText}</StyledTooltipContent>
              </StyledTooltip>
            </Popper>
          </StyledContainer>
        </ClickAwayListener>
      )}
    />
  )
}

export default MarketingConsent
